/**
 * AddWordModal - Modal pour ajouter de nouveaux mots au vocabulaire
 * Fonctionnalités: traduction auto, saisie vocale, catégorisation auto
 */
import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mic, MicOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AddWordModal = ({ isOpen, onClose, onSave, categories = [] }) => {
  const [formData, setFormData] = useState({
    thai_word: "",
    french_meaning: "",
    context: ""
  });
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isAutoTranslating, setIsAutoTranslating] = useState(false);
  const [recordingField, setRecordingField] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const autoTranslateTimeoutRef = useRef(null);

  // Auto-translate when Thai word changes
  const autoTranslate = async (text, field) => {
    if (!text || text.trim().length < 1) return;
    
    setIsAutoTranslating(true);
    try {
      const response = await axios.post(`${API}/translate`, {
        thai_word: field === 'thai' ? text.trim() : null,
        french_word: field === 'french' ? text.trim() : null
      });
      
      if (field === 'thai') {
        // Thai input -> get French translation
        setFormData(prev => ({
          ...prev,
          french_meaning: response.data.french_translation || prev.french_meaning,
          context: response.data.category || prev.context
        }));
      } else {
        // French input -> get Thai translation
        setFormData(prev => ({
          ...prev,
          thai_word: response.data.thai_word || prev.thai_word,
          context: response.data.category || prev.context
        }));
      }
      toast.success("Traduction automatique !");
    } catch (err) {
      console.error("Auto-translate error:", err);
    } finally {
      setIsAutoTranslating(false);
    }
  };

  const handleThaiWordChange = (value) => {
    setFormData(prev => ({ ...prev, thai_word: value }));
    
    if (autoTranslateTimeoutRef.current) {
      clearTimeout(autoTranslateTimeoutRef.current);
    }
    
    // Auto-translate after 1 second of no typing
    autoTranslateTimeoutRef.current = setTimeout(() => {
      if (value.trim().length >= 1) {
        autoTranslate(value, 'thai');
      }
    }, 1000);
  };

  const handleFrenchChange = (value) => {
    setFormData(prev => ({ ...prev, french_meaning: value }));
    
    if (autoTranslateTimeoutRef.current) {
      clearTimeout(autoTranslateTimeoutRef.current);
    }
    
    // Auto-translate after 1 second of no typing
    autoTranslateTimeoutRef.current = setTimeout(() => {
      if (value.trim().length >= 1) {
        autoTranslate(value, 'french');
      }
    }, 1000);
  };

  const startRecording = async (field) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await transcribeAudio(audioBlob, field);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingField(field);
      toast.info("Parlez maintenant...");
    } catch (err) {
      console.error("Microphone error:", err);
      toast.error("Impossible d'accéder au microphone");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob, field) => {
    setIsTranscribing(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('audio', audioBlob, 'recording.webm');
      
      const language = field === 'thai' ? 'th' : 'fr';
      
      const response = await axios.post(
        `${API}/stt?language=${language}`,
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      const transcribedText = response.data.text;
      
      if (field === 'thai') {
        setFormData(prev => ({ ...prev, thai_word: transcribedText }));
        // Auto-translate after voice input
        setTimeout(() => autoTranslate(transcribedText, 'thai'), 500);
      } else if (field === 'french') {
        setFormData(prev => ({ ...prev, french_meaning: transcribedText }));
        // Auto-translate after voice input
        setTimeout(() => autoTranslate(transcribedText, 'french'), 500);
      } else if (field === 'context') {
        setFormData(prev => ({ ...prev, context: transcribedText }));
      }
      
      toast.success("Transcription réussie !");
    } catch (err) {
      console.error("Transcription error:", err);
      toast.error("Erreur de transcription");
    } finally {
      setIsTranscribing(false);
      setRecordingField(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.thai_word || !formData.french_meaning) {
      toast.error("Veuillez remplir les champs obligatoires");
      return;
    }
    onSave(formData);
    setFormData({ thai_word: "", french_meaning: "", context: "" });
  };

  const MicButton = ({ field }) => {
    const isActiveField = recordingField === field;
    
    return (
      <button
        type="button"
        onClick={() => isRecording && isActiveField ? stopRecording() : startRecording(field)}
        disabled={isTranscribing || (isRecording && !isActiveField)}
        className={`p-2 rounded-full transition-all ${
          isActiveField && isRecording
            ? 'bg-red-500 text-white animate-pulse'
            : isTranscribing && isActiveField
            ? 'bg-amber-100 text-amber-600'
            : 'bg-slate-100 hover:bg-amber-100 text-slate-600 hover:text-amber-600'
        } disabled:opacity-50`}
        data-testid={`mic-btn-${field}`}
      >
        {isTranscribing && isActiveField ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : isRecording && isActiveField ? (
          <MicOff className="w-5 h-5" />
        ) : (
          <Mic className="w-5 h-5" />
        )}
      </button>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-[#FDFBF7] border-none">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Ajouter un mot</DialogTitle>
          <DialogDescription>Entrez un mot thaï - traduction et catégorie automatiques !</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <label className="text-sm font-medium text-slate-600 mb-2 block">
              Thaï * {isAutoTranslating && <span className="text-amber-500 ml-2">(traduction...)</span>}
              <span className="text-xs text-slate-400 ml-1">(tapez en thaï → français auto)</span>
            </label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={formData.thai_word}
                  onChange={(e) => handleThaiWordChange(e.target.value)}
                  className="input-underline w-full font-thai text-xl"
                  placeholder="ภาษาไทย"
                  data-testid="input-thai-word"
                />
                {isAutoTranslating && (
                  <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-amber-500" />
                )}
              </div>
              <MicButton field="thai" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600 mb-2 block">
              Français * <span className="text-xs text-slate-400">(tapez en français → thaï auto)</span>
            </label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={formData.french_meaning}
                  onChange={(e) => handleFrenchChange(e.target.value)}
                  className={`input-underline w-full ${isAutoTranslating ? 'bg-amber-50' : ''}`}
                  placeholder="Ou tapez en français..."
                  data-testid="input-french-meaning"
                />
                {isAutoTranslating && (
                  <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-amber-500" />
                )}
              </div>
              <MicButton field="french" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600 mb-2 block">
              Catégorie <span className="text-xs text-slate-400">(auto-rempli ou choisir)</span>
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={formData.context}
                onChange={(e) => setFormData(prev => ({ ...prev, context: e.target.value }))}
                className="input-underline flex-1"
                placeholder="Ex: Salutation, Nourriture..."
                data-testid="input-context"
                list="category-suggestions"
              />
              <datalist id="category-suggestions">
                {categories.map(cat => (
                  <option key={cat} value={cat} />
                ))}
              </datalist>
            </div>
            <p className="text-xs text-slate-400 mt-1">Tapez une nouvelle catégorie pour la créer automatiquement</p>
          </div>
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 rounded-full">
              Annuler
            </Button>
            <Button type="submit" className="flex-1 rounded-full bg-amber-500 hover:bg-amber-600" data-testid="save-word-btn">
              Ajouter
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddWordModal;
